/**
 * Desktop hover preview for the variant thumbnail picker
 * (snippets/variant-thumbnail-picker.liquid).
 *
 * On a device with a real hovering pointer, hovering a fragrance thumbnail previews that scent's
 * name in the below-rail "Selected variant name" text — WITHOUT changing the actual variant.
 *
 * ## Why it never touches variant state
 *
 * The whole thumbnail picker is built on one rule: the native radios are the single source of
 * truth (see the snippet's doc block). So this is a purely visual echo. It writes into the
 * `aria-hidden` selected-value span and nothing else — no radio is checked, no `variant:update`
 * fires, no URL/price/gallery moves. On mouse-out the text is restored from whatever radio is
 * actually `:checked`, so a real selection — or the native section morph that follows one — always
 * wins. There is no stored "original" to go stale: the truth is read live, every time.
 *
 * ## Why document-level delegation
 *
 * `variant-picker.js` morphs the product section on every real change, replacing this markup. A
 * controller holding element references would break after the first selection. Delegating on
 * `document` (with `mouseover`/`mouseout`, which bubble — `mouseenter`/`mouseleave` don't) survives
 * every morph because it resolves the elements fresh on each event.
 *
 * ## Scope
 *
 * Mouse-only by design (`hover: hover`), matching the request. Keyboard users change the real
 * selection via the radios, which updates this text natively — so there's nothing to mirror for
 * them. The preview region is `aria-hidden`, so mutating its text has zero screen-reader impact.
 */

if (!window.__variantThumbnailHoverInit) {
  window.__variantThumbnailHoverInit = true;

  // Touch devices synthesize hover events on tap; gate them out so a tap never leaves a stale
  // preview. Re-evaluated per pointer isn't needed — a session's primary input rarely changes.
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  if (canHover) {
    const PICKER = '.variant-thumbnail-picker';
    const LABEL = '.variant-thumbnail-option__label';
    const VALUE = '.variant-thumbnail-option__selected-value';

    /** The scent name currently selected in a picker — read live from the checked radio. */
    const selectedValue = (picker) => {
      const checked = picker.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    };

    const preview = (label) => {
      const picker = label.closest(PICKER);
      const valueEl = picker?.querySelector(VALUE);
      const radio = label.querySelector('input[type="radio"]');
      // `textContent`, not innerHTML: the radio value is decoded, and this keeps the write inert.
      if (valueEl && radio) valueEl.textContent = radio.value;
    };

    const restore = (picker) => {
      const valueEl = picker.querySelector(VALUE);
      const value = selectedValue(picker);
      if (valueEl && value != null) valueEl.textContent = value;
    };

    document.addEventListener('mouseover', (event) => {
      const label = event.target.closest?.(LABEL);
      if (label) preview(label);
    });

    document.addEventListener('mouseout', (event) => {
      const label = event.target.closest?.(LABEL);
      const picker = label?.closest(PICKER);
      if (!picker) return;

      // Moving between two thumbnails (or onto a label's own child) is not "leaving" — the incoming
      // mouseover will set the next preview. Only restore when the pointer has left the picker's
      // labels entirely (including leaving the window, where relatedTarget is null).
      const to = event.relatedTarget;
      if (to && picker.contains(to) && to.closest(LABEL)) return;
      restore(picker);
    });
  }
}
